import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDiseaseAreas } from "@/api/hooks/diseaseAreas";
import {
  useCreateStudy,
  useDeleteStudy,
  useStudy,
  useUpdateStudy,
} from "@/api/hooks/studies";
import {
  STUDY_KIND_LABELS,
  STUDY_KINDS,
  STUDY_PHASES,
  STUDY_PHASE_LABELS,
  STUDY_STATUSES,
  STUDY_STATUS_LABELS,
  STUDY_TYPES,
  STUDY_TYPE_LABELS,
} from "@/api/labels";
import type { StudyKind, StudyPhase, StudyStatus, StudyType } from "@/api/types";

export default function StudyForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existing, isLoading } = useStudy(id);
  const { data: areas } = useDiseaseAreas();
  const create = useCreateStudy();
  const update = useUpdateStudy(id ?? "");
  const remove = useDeleteStudy();

  const [diseaseAreaId, setDiseaseAreaId] = useState("");
  const [kind, setKind] = useState<StudyKind>("master");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [protocolNumber, setProtocolNumber] = useState("");
  const [nctId, setNctId] = useState("");
  const [phase, setPhase] = useState<StudyPhase | "">("");
  const [studyType, setStudyType] = useState<StudyType>("observational");
  const [statusValue, setStatusValue] = useState<StudyStatus>("planning");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setDiseaseAreaId(existing.disease_area_id);
      setKind(existing.kind);
      setCode(existing.code);
      setName(existing.name);
      setSponsor(existing.sponsor ?? "");
      setProtocolNumber(existing.protocol_number ?? "");
      setNctId(existing.nct_id ?? "");
      setPhase(existing.phase ?? "");
      setStudyType(existing.study_type);
      setStatusValue(existing.status);
    }
  }, [existing]);

  if (isEdit && isLoading) return <CircularProgress />;

  const submit = async () => {
    setError(null);
    const base = {
      code: code.trim(),
      name: name.trim(),
      sponsor: sponsor.trim() || null,
      protocol_number: protocolNumber.trim() || null,
      nct_id: nctId.trim() || null,
      phase: (phase || null) as StudyPhase | null,
      study_type: studyType,
      status: statusValue,
    };
    try {
      if (isEdit) {
        await update.mutateAsync(base);
      } else {
        await create.mutateAsync({
          ...base,
          disease_area_id: diseaseAreaId,
          kind,
        });
      }
      navigate("/clinops/sites/studies");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const canSubmit =
    code.trim() &&
    name.trim() &&
    diseaseAreaId &&
    !create.isPending &&
    !update.isPending;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? "Edit study" : "New study"}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          select
          label="Disease area"
          value={diseaseAreaId}
          onChange={(e) => setDiseaseAreaId(e.target.value)}
          required
          disabled={isEdit}
          helperText={isEdit ? "Cannot change after creation" : undefined}
        >
          {(areas ?? []).map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.code} — {a.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as StudyKind)}
          required
          disabled={isEdit}
          helperText={isEdit ? "Cannot change after creation" : undefined}
        >
          {STUDY_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {STUDY_KIND_LABELS[k]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          helperText="Unique within (disease area, kind). For master rows this typically equals the disease-area code."
        />
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField label="Sponsor" value={sponsor} onChange={(e) => setSponsor(e.target.value)} />
        <TextField
          label="Protocol number"
          value={protocolNumber}
          onChange={(e) => setProtocolNumber(e.target.value)}
        />
        <TextField label="NCT ID" value={nctId} onChange={(e) => setNctId(e.target.value)} />
        <TextField
          select
          label="Phase"
          value={phase}
          onChange={(e) => setPhase(e.target.value as StudyPhase | "")}
        >
          <MenuItem value="">—</MenuItem>
          {STUDY_PHASES.map((p) => (
            <MenuItem key={p} value={p}>
              {STUDY_PHASE_LABELS[p]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Study type"
          value={studyType}
          onChange={(e) => setStudyType(e.target.value as StudyType)}
          required
        >
          {STUDY_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {STUDY_TYPE_LABELS[t]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={statusValue}
          onChange={(e) => setStatusValue(e.target.value as StudyStatus)}
          required
        >
          {STUDY_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {STUDY_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Box>
            {isEdit && (
              <Button
                color="error"
                onClick={async () => {
                  if (!id) return;
                  if (!confirm("Delete this study?")) return;
                  try {
                    await remove.mutateAsync(id);
                    navigate("/clinops/sites/studies");
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Failed to delete");
                  }
                }}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => navigate("/clinops/sites/studies")}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={!canSubmit}>
              {isEdit ? "Save" : "Create"}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
